import React, { useEffect } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { CreateEmpresaPayload, Empresa, UpdateEmpresaPayload } from '../types';
import { useCreateEmpresaMutation, useUpdateEmpresaMutation } from '../api/empresasApiSlice';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../../../features/auth/authSlice';
import { showToast } from '../../../components/ui/Toast';
import { Logger } from '../../../lib/utils';

interface EmpresaFormProps {
  mode: 'create' | 'edit';
  empresa?: Empresa | null;
  onClose: () => void;
  onSubmitSuccess?: () => void;
}

export const EmpresaForm = ({ mode, empresa, onClose, onSubmitSuccess }: EmpresaFormProps) => {
  Logger.debug("EmpresaForm renderizado:", { mode, empresa });
  
  // Obtener usuario actual para verificar si es superadmin
  const currentUser = useSelector(selectCurrentUser);
  const isSuperAdmin = currentUser?.role === 'superadmin';
  
  // Inicializar formulario con react-hook-form
  const { register, handleSubmit, formState: { errors }, watch, reset, getValues } = useForm<CreateEmpresaPayload>({
    defaultValues: mode === 'edit' && empresa
      ? {
          nombre: empresa.nombre,
          descripcion: empresa.descripcion || '',
          ntcliente1: empresa.ntcliente1 || '',
          ntcliente2: empresa.ntcliente2 || '',
          ntcliente3: empresa.ntcliente3 || '',
          ntcliente4: empresa.ntcliente4 || '',
          ntcliente5: empresa.ntcliente5 || '',
          // Valores por defecto para todos los bots
          ...Array.from({ length: 10 }, (_, i) => i + 1).reduce((acc, botNum) => ({
            ...acc,
            [`bot${botNum}_nombre`]: empresa[`bot${botNum}_nombre` as keyof Empresa] || '',
            [`bot${botNum}_enabled`]: empresa[`bot${botNum}_enabled` as keyof Empresa] || false,
            [`bot${botNum}_texto1`]: empresa[`bot${botNum}_texto1` as keyof Empresa] || '',
            [`bot${botNum}_texto2`]: empresa[`bot${botNum}_texto2` as keyof Empresa] || '',
            [`bot${botNum}_texto3`]: empresa[`bot${botNum}_texto3` as keyof Empresa] || '',
            [`bot${botNum}_texto4`]: empresa[`bot${botNum}_texto4` as keyof Empresa] || '',
            [`bot${botNum}_texto5`]: empresa[`bot${botNum}_texto5` as keyof Empresa] || '',
            [`bot${botNum}_nombre1`]: empresa[`bot${botNum}_nombre1` as keyof Empresa] || '',
            [`bot${botNum}_nombre2`]: empresa[`bot${botNum}_nombre2` as keyof Empresa] || '',
            [`bot${botNum}_nombre3`]: empresa[`bot${botNum}_nombre3` as keyof Empresa] || '',
            [`bot${botNum}_nombre4`]: empresa[`bot${botNum}_nombre4` as keyof Empresa] || '',
            [`bot${botNum}_nombre5`]: empresa[`bot${botNum}_nombre5` as keyof Empresa] || '',
            [`bot${botNum}_enabled1`]: empresa[`bot${botNum}_enabled1` as keyof Empresa] || false,
            [`bot${botNum}_enabled2`]: empresa[`bot${botNum}_enabled2` as keyof Empresa] || false,
            [`bot${botNum}_enabled3`]: empresa[`bot${botNum}_enabled3` as keyof Empresa] || false,
            [`bot${botNum}_enabled4`]: empresa[`bot${botNum}_enabled4` as keyof Empresa] || false,
            [`bot${botNum}_enabled5`]: empresa[`bot${botNum}_enabled5` as keyof Empresa] || false,
          }), {})
        }
      : {
          nombre: '',
          descripcion: '',
          ntcliente1: '',
          ntcliente2: '',
          ntcliente3: '',
          ntcliente4: '',
          ntcliente5: '',
          // Valores por defecto para todos los bots
          ...Array.from({ length: 10 }, (_, i) => i + 1).reduce((acc, botNum) => ({
            ...acc,
            [`bot${botNum}_nombre`]: '',
            [`bot${botNum}_enabled`]: false,
            [`bot${botNum}_texto1`]: '',
            [`bot${botNum}_texto2`]: '',
            [`bot${botNum}_texto3`]: '',
            [`bot${botNum}_texto4`]: '',
            [`bot${botNum}_texto5`]: '',
            [`bot${botNum}_nombre1`]: '',
            [`bot${botNum}_nombre2`]: '',
            [`bot${botNum}_nombre3`]: '',
            [`bot${botNum}_nombre4`]: '',
            [`bot${botNum}_nombre5`]: '',
            [`bot${botNum}_enabled1`]: false,
            [`bot${botNum}_enabled2`]: false,
            [`bot${botNum}_enabled3`]: false,
            [`bot${botNum}_enabled4`]: false,
            [`bot${botNum}_enabled5`]: false,
          }), {})
        }
  });
  
  // Actualizar valores del formulario cuando cambia la empresa
  useEffect(() => {
    if (mode === 'edit' && empresa) {
      Logger.debug("Reseteando formulario con datos:", empresa);
      Logger.debug("Valores de ntcliente en empresa:", {
        ntcliente1: empresa.ntcliente1,
        ntcliente2: empresa.ntcliente2,
        ntcliente3: empresa.ntcliente3,
        ntcliente4: empresa.ntcliente4,
        ntcliente5: empresa.ntcliente5,
        tipo: typeof empresa.ntcliente1
      });
      
      // Verificar el objeto empresa completo para depuración
      Logger.debug("Estructura completa del objeto empresa:", JSON.stringify(empresa));
      
      // Asegurarnos de que los valores se establecen correctamente
      const formValues = {
        nombre: empresa.nombre,
        descripcion: empresa.descripcion || '',
        ntcliente1: empresa.ntcliente1 || '',
        ntcliente2: empresa.ntcliente2 || '',
        ntcliente3: empresa.ntcliente3 || '',
        ntcliente4: empresa.ntcliente4 || '',
        ntcliente5: empresa.ntcliente5 || '',
        // Valores por defecto para todos los bots
        ...Array.from({ length: 10 }, (_, i) => i + 1).reduce((acc, botNum) => ({
          ...acc,
          [`bot${botNum}_nombre`]: empresa[`bot${botNum}_nombre` as keyof Empresa] || '',
          [`bot${botNum}_enabled`]: empresa[`bot${botNum}_enabled` as keyof Empresa] || false,
          [`bot${botNum}_texto1`]: empresa[`bot${botNum}_texto1` as keyof Empresa] || '',
          [`bot${botNum}_texto2`]: empresa[`bot${botNum}_texto2` as keyof Empresa] || '',
          [`bot${botNum}_texto3`]: empresa[`bot${botNum}_texto3` as keyof Empresa] || '',
          [`bot${botNum}_texto4`]: empresa[`bot${botNum}_texto4` as keyof Empresa] || '',
          [`bot${botNum}_texto5`]: empresa[`bot${botNum}_texto5` as keyof Empresa] || '',
          [`bot${botNum}_nombre1`]: empresa[`bot${botNum}_nombre1` as keyof Empresa] || '',
          [`bot${botNum}_nombre2`]: empresa[`bot${botNum}_nombre2` as keyof Empresa] || '',
          [`bot${botNum}_nombre3`]: empresa[`bot${botNum}_nombre3` as keyof Empresa] || '',
          [`bot${botNum}_nombre4`]: empresa[`bot${botNum}_nombre4` as keyof Empresa] || '',
          [`bot${botNum}_nombre5`]: empresa[`bot${botNum}_nombre5` as keyof Empresa] || '',
          [`bot${botNum}_enabled1`]: empresa[`bot${botNum}_enabled1` as keyof Empresa] || false,
          [`bot${botNum}_enabled2`]: empresa[`bot${botNum}_enabled2` as keyof Empresa] || false,
          [`bot${botNum}_enabled3`]: empresa[`bot${botNum}_enabled3` as keyof Empresa] || false,
          [`bot${botNum}_enabled4`]: empresa[`bot${botNum}_enabled4` as keyof Empresa] || false,
          [`bot${botNum}_enabled5`]: empresa[`bot${botNum}_enabled5` as keyof Empresa] || false,
        }), {})
      };
      
      Logger.debug("Valores a asignar al formulario:", {
        ntcliente1: formValues.ntcliente1,
        ntcliente2: formValues.ntcliente2,
        ntcliente3: formValues.ntcliente3,
        ntcliente4: formValues.ntcliente4,
        ntcliente5: formValues.ntcliente5
      });
      
      reset(formValues);
      
      // Verificar después de reset si los valores se asignaron correctamente
      setTimeout(() => {
        const currentValues = getValues();
        Logger.debug("Valores actuales del formulario después de reset:", {
          ntcliente1: currentValues.ntcliente1,
          ntcliente2: currentValues.ntcliente2,
          ntcliente3: currentValues.ntcliente3,
          ntcliente4: currentValues.ntcliente4,
          ntcliente5: currentValues.ntcliente5
        });
      }, 100);
    }
  }, [empresa, mode, reset, getValues]);
  
  // Observar los valores de los campos ntcliente para depuración
  const ntcliente1Value = watch('ntcliente1');
  const ntcliente2Value = watch('ntcliente2');
  const ntcliente3Value = watch('ntcliente3');
  const ntcliente4Value = watch('ntcliente4');
  const ntcliente5Value = watch('ntcliente5');
  
  useEffect(() => {
    Logger.debug("Valores actuales de ntcliente:", {
      ntcliente1: ntcliente1Value,
      ntcliente2: ntcliente2Value,
      ntcliente3: ntcliente3Value,
      ntcliente4: ntcliente4Value,
      ntcliente5: ntcliente5Value
    });
  }, [ntcliente1Value, ntcliente2Value, ntcliente3Value, ntcliente4Value, ntcliente5Value]);
  
  // Hooks de RTK Query para mutaciones
  const [createEmpresa, { isLoading: isCreating, error: createError }] = useCreateEmpresaMutation();
  const [updateEmpresa, { isLoading: isUpdating, error: updateError }] = useUpdateEmpresaMutation();
  
  // Determinar estado de carga y error
  const isLoading = isCreating || isUpdating;
  const error = createError || updateError;
  
  // Mostrar error si existe
  useEffect(() => {
    if (error) {
      showToast('Error al procesar el formulario', 'error');
    }
  }, [error]);
  
  // Enviar el formulario
  const onSubmit: SubmitHandler<CreateEmpresaPayload> = async (data) => {
    // Crear una copia de los datos para formatearlos
    const formattedData = { ...data };

    // Convertir campos bot*_enabled y bot*_enabled* a booleanos explícitamente
    for (let i = 1; i <= 10; i++) {
      const botEnabledKey = `bot${i}_enabled` as keyof typeof data;
      
      if (botEnabledKey in data) {
        formattedData[botEnabledKey] = Boolean(data[botEnabledKey]);
      }
      
      // También manejar los campos bot*_enabled*
      for (let j = 1; j <= 5; j++) {
        const fieldEnabledKey = `bot${i}_enabled${j}` as keyof typeof data;
        
        if (fieldEnabledKey in data) {
          formattedData[fieldEnabledKey] = Boolean(data[fieldEnabledKey]);
        }
      }
    }

    try {
      if (mode === 'create') {
        await createEmpresa(formattedData as CreateEmpresaPayload).unwrap();
        showToast('Empresa creada correctamente', 'success');
        reset();
        onSubmitSuccess?.();
      } else if (empresa?.id) {
        await updateEmpresa({
          id: empresa.id,
          changes: formattedData as UpdateEmpresaPayload
        }).unwrap();
        showToast('Empresa actualizada correctamente', 'success');
        onSubmitSuccess?.();
      }
    } catch (error: unknown) {
      const err = error as { status?: number; data?: { code?: string } };
      if (err?.status === 409 || (err?.data && err?.data.code === 'DUPLICATE_NAME')) {
        showToast('Ya existe una empresa con ese nombre', 'error');
      } else {
        showToast('Error al procesar la empresa', 'error');
      }
    }
  };

  // Componente para la configuración de un bot
  const BotConfig = ({ botNumber }: { botNumber: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 }) => {
    const botPrefix = `bot${botNumber}`;
    const isEnabled = watch(`${botPrefix}_enabled` as keyof CreateEmpresaPayload) as boolean;
    
    return (
      <div className="border border-border bg-card/80 p-4 rounded-md mb-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-medium text-foreground">Bot {botNumber}</h4>
          <div className="flex items-center">
            <input
              type="checkbox"
              id={`${botPrefix}_enabled`}
              {...register(`${botPrefix}_enabled` as keyof CreateEmpresaPayload)}
              className="h-4 w-4 text-primary focus:ring-primary border-border rounded"
              disabled={isLoading}
            />
            <label htmlFor={`${botPrefix}_enabled`} className="ml-2 text-xs text-foreground">
              Habilitado
            </label>
          </div>
        </div>
        
        {isEnabled && (
          <>
            {/* Nombre del bot */}
            <div className="mb-4">
              <label 
                htmlFor={`${botPrefix}_nombre`} 
                className="block text-xs font-medium text-foreground"
              >
                Nombre del bot
              </label>
              <input
                type="text"
                id={`${botPrefix}_nombre`}
                {...register(`${botPrefix}_nombre` as keyof CreateEmpresaPayload, { 
                  required: "El nombre del bot es requerido cuando está habilitado"
                })}
                className={`mt-1 block w-full px-2 py-1.5 bg-background text-foreground border ${errors[`${botPrefix}_nombre` as keyof CreateEmpresaPayload] ? 'border-destructive' : 'border-border'} rounded-sm shadow-sm focus:outline-none focus:ring-primary focus:border-primary text-sm`}
                disabled={isLoading}
                placeholder="Nombre del bot"
              />
              {errors[`${botPrefix}_nombre` as keyof CreateEmpresaPayload] && (
                <p className="mt-1 text-xs text-destructive">
                  {errors[`${botPrefix}_nombre` as keyof CreateEmpresaPayload]?.message}
                </p>
              )}
            </div>
            
            {/* Campos de texto del bot */}
            {[1, 2, 3, 4, 5].map((num) => {
              const fieldEnabled = watch(`${botPrefix}_enabled${num}` as keyof CreateEmpresaPayload) as boolean;
              
              return (
                <div key={`${botPrefix}-campo-${num}`} className="mb-4 p-3 border border-border rounded-md">
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="text-xs font-medium text-foreground">Campo {num}</h5>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id={`${botPrefix}_enabled${num}`}
                        {...register(`${botPrefix}_enabled${num}` as keyof CreateEmpresaPayload)}
                        className="h-4 w-4 text-primary focus:ring-primary border-border rounded"
                        disabled={isLoading}
                      />
                      <label htmlFor={`${botPrefix}_enabled${num}`} className="ml-2 text-xs text-foreground">
                        Habilitado
                      </label>
                    </div>
                  </div>
                  
                  {fieldEnabled && (
                    <>
                      {/* Nombre del campo */}
                      <div className="mb-2">
                        <label 
                          htmlFor={`${botPrefix}_nombre${num}`} 
                          className="block text-xs font-medium text-foreground"
                        >
                          Nombre del campo
                        </label>
                        <input
                          type="text"
                          id={`${botPrefix}_nombre${num}`}
                          {...register(`${botPrefix}_nombre${num}` as keyof CreateEmpresaPayload, { 
                            required: "El nombre del campo es requerido cuando está habilitado"
                          })}
                          className={`mt-1 block w-full px-2 py-1.5 bg-background text-foreground border ${errors[`${botPrefix}_nombre${num}` as keyof CreateEmpresaPayload] ? 'border-destructive' : 'border-border'} rounded-sm shadow-sm focus:outline-none focus:ring-primary focus:border-primary text-sm`}
                          disabled={isLoading}
                          placeholder="Nombre del campo"
                        />
                        {errors[`${botPrefix}_nombre${num}` as keyof CreateEmpresaPayload] && (
                          <p className="mt-1 text-xs text-destructive">
                            {errors[`${botPrefix}_nombre${num}` as keyof CreateEmpresaPayload]?.message}
                          </p>
                        )}
                      </div>
                      
                      {/* Texto del campo */}
                      <div className="mb-2">
                        <label 
                          htmlFor={`${botPrefix}_texto${num}`} 
                          className="block text-xs font-medium text-foreground"
                        >
                          Texto del campo
                        </label>
                        <textarea
                          id={`${botPrefix}_texto${num}`}
                          {...register(`${botPrefix}_texto${num}` as keyof CreateEmpresaPayload)}
                          rows={3}
                          className={`mt-1 block w-full px-2 py-1.5 bg-background text-foreground border ${errors[`${botPrefix}_texto${num}` as keyof CreateEmpresaPayload] ? 'border-destructive' : 'border-border'} rounded-sm shadow-sm focus:outline-none focus:ring-primary focus:border-primary text-sm`}
                          disabled={isLoading}
                          placeholder="Texto del campo"
                        />
                        {errors[`${botPrefix}_texto${num}` as keyof CreateEmpresaPayload] && (
                          <p className="mt-1 text-xs text-destructive">
                            {errors[`${botPrefix}_texto${num}` as keyof CreateEmpresaPayload]?.message}
                          </p>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-foreground/20 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-card text-card-foreground rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-foreground">
              {mode === 'create' ? 'Crear Nueva Empresa' : 'Editar Empresa'}
            </h2>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground"
              disabled={isLoading}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Sección de información básica */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-foreground">Información Básica</h3>
              
              {/* Campo Nombre */}
              <div>
                <label htmlFor="nombre" className="block text-sm font-medium text-foreground mb-1">
                  Nombre de Empresa
                </label>
                <input
                  id="nombre"
                  type="text"
                  {...register("nombre", { 
                    required: "El nombre es obligatorio",
                    minLength: { value: 3, message: "El nombre debe tener al menos 3 caracteres" }
                  })}
                  className={`block w-full px-3 py-2 bg-background text-foreground border ${errors.nombre ? 'border-destructive' : 'border-border'} rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm`}
                  disabled={isLoading}
                />
                {errors.nombre && (
                  <p className="mt-1 text-sm text-destructive">{errors.nombre.message}</p>
                )}
              </div>
              
              {/* Campo Descripción */}
              <div>
                <label htmlFor="descripcion" className="block text-sm font-medium text-foreground mb-1">
                  Descripción
                </label>
                <textarea
                  id="descripcion"
                  {...register("descripcion")}
                  rows={4}
                  className={`block w-full px-3 py-2 bg-background text-foreground border ${errors.descripcion ? 'border-destructive' : 'border-border'} rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm`}
                  disabled={isLoading}
                />
                {errors.descripcion && (
                  <p className="mt-1 text-sm text-destructive">{errors.descripcion.message}</p>
                )}
              </div>
            </div>
            
            {/* Sección de configuración de campos de clientes (solo para superadmin) */}
            {isSuperAdmin && (
              <div className="space-y-4 pt-6 border-t border-border">
                <h3 className="text-lg font-medium text-foreground">Configuración de Campos de Clientes</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Configure los nombres personalizados para los campos de texto de los clientes de esta empresa.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[1, 2, 3, 4, 5].map((num) => (
                    <div key={`ntcliente${num}`}>
                      <label 
                        htmlFor={`ntcliente${num}`} 
                        className="block text-sm font-medium text-foreground mb-1"
                      >
                        Nombre Campo {num}
                      </label>
                      <input
                        type="text"
                        id={`ntcliente${num}`}
                        {...register(`ntcliente${num}` as keyof CreateEmpresaPayload)}
                        className="block w-full px-3 py-2 bg-background text-foreground border border-border rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                        placeholder={`Campo ${num}`}
                        disabled={isLoading}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Sección de configuración de bots (solo para superadmin) */}
            {isSuperAdmin && (
              <div className="space-y-4 pt-6 border-t border-border">
                <h3 className="text-lg font-medium text-foreground">Configuración de Bots</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Configure los bots y sus campos de texto para esta empresa.
                  Cada bot puede tener hasta 5 campos de texto configurables.
                </p>
                
                {/* Renderizar los 10 bots */}
                <BotConfig botNumber={1} />
                <BotConfig botNumber={2} />
                <BotConfig botNumber={3} />
                <BotConfig botNumber={4} />
                <BotConfig botNumber={5} />
                <BotConfig botNumber={6} />
                <BotConfig botNumber={7} />
                <BotConfig botNumber={8} />
                <BotConfig botNumber={9} />
                <BotConfig botNumber={10} />
              </div>
            )}
            
            {/* Botones de acción */}
            <div className="pt-4 flex justify-end space-x-3 border-t border-border">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-muted text-foreground rounded-md hover:bg-accent"
                disabled={isLoading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
                disabled={isLoading}
              >
                {isLoading 
                  ? (mode === 'create' ? 'Creando...' : 'Guardando...') 
                  : (mode === 'create' ? 'Crear Empresa' : 'Guardar Cambios')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};